import _ from 'lodash';
import { ILaizeRoom } from '../Components/Laizes/LaizeCalc';
import { MapConstants } from '../MapConstants';
import { IRoomItem } from '../Models/IRoomItem';
import { Editor } from './Editor';
import { JstsPolygon, JstsUtils } from './JstsUtils';
import { qSVG } from './qSVG';
import {
    BoundingBox,
    CoordPoint,
    Equation,
    LineSegment,
    RotationParams,
    Wall,
    WallLine,
    WallWithCorrection,
} from './Types';
import { WallUtils } from './Wall/WallUtils';

type BoundingBoxParams = {
    walls?: Wall[];
    points?: Array<CoordPoint>;
    roomItems?: Array<IRoomItem>;
    includeBands?: Array<CoordPoint>;
};

const reducer = (accumulator: number, currentValue: number) => accumulator + currentValue;

export class PointUtils {
    public static toPosition = (coord: CoordPoint, round?: boolean): CoordPoint | undefined => {
        if (coord?.x && coord?.y) {
            return {
                x: round ? Math.round(coord.x) : coord.x,
                y: round ? Math.round(coord.y) : coord.y,
            };
        }
        return undefined;
    };

    public static translationByMeanPoint(points: Array<CoordPoint>, destinationPoint: CoordPoint, opposite?: boolean) {
        const mp = this.meanPoint(points);

        points.forEach((point) => {
            const newPoints = !opposite
                ? {
                      x: point.x + destinationPoint.x - mp.x,
                      y: point.y + destinationPoint.y - mp.y,
                  }
                : {
                      x: point.x - destinationPoint.x + mp.x,
                      y: point.y - destinationPoint.y + mp.y,
                  };
            point.x = newPoints.x;
            point.y = newPoints.y;
        });
    }

    public static meanPoint(points: Array<CoordPoint>) {
        return {
            x: Array.from(points, (p) => p.x).reduce(reducer) / points.length,
            y: Array.from(points, (p) => p.y).reduce(reducer) / points.length,
        };
    }

    public static convertToCoordinates(point: CoordPoint, roundPoints?: boolean) {
        const meter = MapConstants.meter;
        if (roundPoints) {
            point.x = Math.round(point.x * meter);
            point.y = Math.round(point.y * meter);
        } else {
            point.x *= meter;
            point.y *= meter;
        }
        return point;
    }

    public static placeToZero(points: Array<CoordPoint>) {
        const topLeftCorner = {
            x: Math.abs(Math.min(...points.map((point) => point.x))),
            y: Math.abs(Math.min(...points.map((point) => point.y))),
        };

        if (topLeftCorner.x !== 0 || topLeftCorner.y !== 0) {
            points.forEach((point) => {
                const newPoint = this.translation(point, topLeftCorner);
                point.x = newPoint.x;
                point.y = newPoint.y;
            });
        }
    }

    public static translation(point: CoordPoint, vector: CoordPoint, opposite?: boolean): CoordPoint {
        var newPoints = !opposite
            ? {
                  x: point.x + vector.x,
                  y: point.y + vector.y,
              }
            : {
                  x: point.x - vector.x,
                  y: point.y - vector.y,
              };
        return newPoints;
    }

    public static placeAlongSideExistingRooms(points: Array<CoordPoint>, walls: Wall[]) {
        const meter = MapConstants.meter;
        const boundingBox = this.calculateBoundingBox({ walls });

        const topRightBoundingBox = boundingBox
            ? { x: boundingBox.xMax, y: boundingBox.yMin }
            : { x: 40 * meter, y: 80 * meter };

        topRightBoundingBox.x += MapConstants.roomDefaultSpacing;

        points.forEach((point) => {
            const newPoint = this.translation(point, topRightBoundingBox);
            point.x = newPoint.x;
            point.y = newPoint.y;
        });
    }

    public static arePointsEquals(pointA: CoordPoint, pointB: CoordPoint) {
        return pointA.x === pointB.x && pointA.y === pointB.y;
    }

    public static arePointsNearlyEquals(pointA: CoordPoint, pointB: CoordPoint) {
        return (
            pointA.x >= pointB.x - 0.001 &&
            pointA.x <= pointB.x + 0.001 &&
            pointA.y >= pointB.y - 0.001 &&
            pointA.y <= pointB.y + 0.001
        );
    }

    // /**
    //  * Determines maximums and minimums on the x and y axis for a series of points.
    //  * @param {*} points If left undefined, uses WALLS starting points.
    //  * @returns BoundingBox: {xMin: number, xMax: number, yMin: number, yMax: number}
    //  */
    // public static calculateBoundingBox(walls: CircularLinkedList<Wall>, roomItems: Array<IRoomItem> = [], points?: Array<CoordPoint>, includeRoomItems = false): BoundingBox | undefined {
    //     let allPoints: Array<CoordPoint> = [];

    //     if (!points) {
    //         walls.forEach((wall) => allPoints.push(wall.start));

    //         if (includeRoomItems) {
    //             roomItems.forEach((roomItem: IRoomItem) => {
    //                 if (roomItem.isMoveableOutside) {
    //                     roomItem.coordsReal?.forEach((coord) => allPoints.push(coord));
    //                 }
    //             });
    //         }
    //     } else {
    //         allPoints = points;
    //     }

    //     const xSeries = allPoints.map((point) => point.x);
    //     const ySeries = allPoints.map((point) => point.y);

    //     return xSeries.length > 0 && ySeries.length > 0
    //         ? {
    //             xMin: Math.min(...xSeries),
    //             xMax: Math.max(...xSeries),
    //             yMin: Math.min(...ySeries),
    //             yMax: Math.max(...ySeries),
    //         }
    //         : undefined;
    // }

    /**
     * Determines maximums and minimums on the x and y axis for a series of points.
     * @param {*} points If left undefined, uses WALLS starting points.
     * @returns {} {xMin: number, xMax: number, yMin: number, yMax: number}
     */
    public static calculateBoundingBox({
        points,
        walls = [],
        roomItems,
        includeBands,
    }: BoundingBoxParams): BoundingBox | undefined {
        let allPoints = [];
        if (!points) {
            walls.forEach((wall) => allPoints.push(wall.start));
            if (roomItems) {
                roomItems.forEach((roomItem) => {
                    if (roomItem.isMoveableOutside) {
                        roomItem.coordsReal?.forEach((coord) => allPoints.push(coord));
                    }
                });
            }
            if (includeBands) {
                allPoints.push(...includeBands);
            }
        } else {
            allPoints = points;
        }

        const xSeries = allPoints.map((point) => point.x);
        const ySeries = allPoints.map((point) => point.y);

        return xSeries.length > 0 && ySeries.length > 0
            ? {
                  xMin: Math.min(...xSeries),
                  xMax: Math.max(...xSeries),
                  yMin: Math.min(...ySeries),
                  yMax: Math.max(...ySeries),
              }
            : undefined;
    }

    public static increaseBoundingBox(boundingBox: BoundingBox, size: number) {
        return {
            xMin: boundingBox.xMin - size,
            xMax: boundingBox.xMax + size,
            yMin: boundingBox.yMin - size,
            yMax: boundingBox.yMax + size,
        };
    }

    public static isInsideBoundingBox(point: CoordPoint, boundingBox: BoundingBox) {
        return (
            point.x >= boundingBox.xMin &&
            point.x <= boundingBox.xMax &&
            point.y >= boundingBox.yMin &&
            point.y <= boundingBox.yMax
        );
    }

    public static smoothOutPoints(points: Array<CoordPoint>) {
        for (const point of points) {
            this.smoothOutPoint(point);
        }
    }

    public static smoothOutPoint(point: CoordPoint) {
        point.x = _.round(point.x, 1);
        point.y = _.round(point.y, 1);
    }

    public static degreeToRad(degrees: number) {
        return degrees * (Math.PI / 180);
    }

    public static distanceBetweenPoints(pointA: CoordPoint, pointB: CoordPoint) {
        return Math.sqrt(Math.pow(pointB.x - pointA.x, 2) + Math.pow(pointB.y - pointA.y, 2));
    }

    public static inflatePolygon(poly: Array<CoordPoint> = [], spacing: number) {
        const geoInput = JstsUtils.vectorCoordinates2JTS(poly);
        geoInput.push(geoInput[0]);
        const polygon = JstsUtils.buffer(geoInput, spacing);
        let inflatedCoordinates: Array<CoordPoint> = [];
        const coordinates = polygon.getCoordinates();

        for (let i = 0; i < coordinates.length; i++) {
            const point = coordinates[i];
            inflatedCoordinates.push({ x: point.x, y: point.y });
        }
        return inflatedCoordinates;
    }

    public static limitObj = (
        equation: Equation,
        size: number,
        coords: CoordPoint,
        message?: string
    ): Array<CoordPoint> => {
        //equation à fix lorsqu'une ouverture est sur un angle
        if (message) console.log(message);
        const Px = coords.x;
        const Py = coords.y;
        const Aq = equation.A;
        const Bq = equation.B;

        let pos1;
        let pos2;
        if (Aq === 'v') {
            pos1 = { x: Px, y: Py - size / 2 };
            pos2 = { x: Px, y: Py + size / 2 };
        } else if (Aq === 'h') {
            pos1 = { x: Px - size / 2, y: Py };
            pos2 = { x: Px + size / 2, y: Py };
        } else {
            const A = 1 + Aq * Aq;
            const B = -2 * Px + 2 * Aq * Bq + -2 * Py * Aq;
            const C = Px * Px + Bq * Bq - 2 * Py * Bq + Py * Py - (size * size) / 4; // -N
            const Delta = B * B - 4 * A * C;
            const posX1 = (-B - Math.sqrt(Delta)) / (2 * A);
            const posX2 = (-B + Math.sqrt(Delta)) / (2 * A);
            pos1 = { x: posX1, y: Aq * posX1 + Bq };
            pos2 = { x: posX2, y: Aq * posX2 + Bq };
        }
        return [pos1, pos2];
    };

    public static getSupperposedPoints(points: Array<CoordPoint>): CoordPoint | undefined {
        for (let i = 0; i < points.length; i++) {
            for (let j = 0; j < points.length; j++) {
                if (i !== j && points[i].x === points[j].x && points[i].y === points[j].y) {
                    return points[i];
                }
            }
        }
        return undefined;
    }

    public static rotation(points: Array<CoordPoint>, degrees: number, rotationCenter: CoordPoint = { x: 0, y: 0 }) {
        const rad = this.degreeToRad(degrees);
        points.forEach((point) => {
            PointUtils.singleRotation(point, rad, rotationCenter);
        });
    }

    public static singleRotation(point: CoordPoint, rad: number, rotationCenter: CoordPoint = { x: 0, y: 0 }) {
        const tPoint = {
            x: point.x - rotationCenter.x,
            y: point.y - rotationCenter.y,
        };

        const newPoints = {
            x: tPoint.x * Math.cos(rad) - tPoint.y * Math.sin(rad) + rotationCenter.x,
            y: tPoint.x * Math.sin(rad) + tPoint.y * Math.cos(rad) + rotationCenter.y,
        };
        point.x = newPoints.x;
        point.y = newPoints.y;
    }

    public static intersect(l1: LineSegment, l2: LineSegment) {
        // Check if none of the lines are of length 0
        if ((l1.p1.x === l1.p2.x && l1.p1.y === l1.p2.y) || (l2.p1.x === l2.p2.x && l2.p1.y === l2.p2.y)) {
            return false;
        }

        const denominator = (l2.p2.y - l2.p1.y) * (l1.p2.x - l1.p1.x) - (l2.p2.x - l2.p1.x) * (l1.p2.y - l1.p1.y);

        // Lines are parallel
        if (denominator === 0) {
            return false;
        }

        let ua = ((l2.p2.x - l2.p1.x) * (l1.p1.y - l2.p1.y) - (l2.p2.y - l2.p1.y) * (l1.p1.x - l2.p1.x)) / denominator;
        let ub = ((l1.p2.x - l1.p1.x) * (l1.p1.y - l2.p1.y) - (l1.p2.y - l1.p1.y) * (l1.p1.x - l2.p1.x)) / denominator;

        // is the intersection along the segments
        if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
            return false;
        }

        // Return a object with the x and y coordinates of the intersection
        let x = l1.p1.x + ua * (l1.p2.x - l1.p1.x);
        let y = l1.p1.y + ua * (l1.p2.y - l1.p1.y);

        return { x, y };
    }

    /**
     * Returns the perpendicular distance from `point` to the infinite line defined by segment AB.
     * If A and B are the same point, returns the distance from `point` to A.
     */
    public static pointToLineDistance(point: CoordPoint, a: CoordPoint, b: CoordPoint): number {
        const vx = b.x - a.x;
        const vy = b.y - a.y;
        const wx = point.x - a.x;
        const wy = point.y - a.y;

        const len2 = vx * vx + vy * vy;
        if (len2 === 0) {
            return this.distanceBetweenPoints(point, a);
        }
        // |v x w| / |v|
        const cross = Math.abs(vx * wy - vy * wx);
        return cross / Math.sqrt(len2);
    }

    public static isPointOnSegment(p: CoordPoint, a: CoordPoint, b: CoordPoint, epsilon = 1e-6): boolean {
        const cross = (b.y - a.y) * (p.x - a.x) - (b.x - a.x) * (p.y - a.y);
        if (Math.abs(cross) > epsilon) {
            return false;
        }
        const dot = (p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y);
        if (dot < 0) {
            return false;
        }
        const lenSq = Math.pow((b.x - a.x), 2) + Math.pow((b.y - a.y), 2);
        if (dot > lenSq) {
            return false;
        }
        return true;
    }

    /**
     * Returns true if vectors v1 (a->b) and v2 (c->d) are parallel within an angular tolerance.
     */
    private static areVectorsParallel(
        a: CoordPoint,
        b: CoordPoint,
        c: CoordPoint,
        d: CoordPoint,
        angleToleranceDeg: number
    ): boolean {
        const v1x = b.x - a.x;
        const v1y = b.y - a.y;
        const v2x = d.x - c.x;
        const v2y = d.y - c.y;

        const n1 = Math.hypot(v1x, v1y);
        const n2 = Math.hypot(v2x, v2y);
        if (n1 === 0 || n2 === 0) {
            // If either is degenerate, treat as parallel check not applicable here.
            return true;
        }

        // sin of angle between vectors = |v1 x v2| / (|v1||v2|)
        const sinAngle = Math.abs(v1x * v2y - v1y * v2x) / (n1 * n2);
        const sinTol = Math.sin(this.degreeToRad(angleToleranceDeg));
        return sinAngle <= sinTol;
    }

    /**
     * Detects if two line segments are collinear or nearly collinear.
     * - Collinear: both points of one segment lie on the infinite line of the other (within distance tolerance),
     *   and directions are parallel within an angular tolerance.
     *
     * @param l1 First segment
     * @param l2 Second segment
     * @param angleToleranceDeg Maximum angular difference (in degrees) to consider as parallel (default: 1°)
     * @param distanceTolerance Maximum perpendicular distance to the line to consider as collinear (default: 1e-3)
     * @returns true if the segments are collinear or nearly collinear
     */
    public static areSegmentsCollinear(
        l1: LineSegment,
        l2: LineSegment,
        angleToleranceDeg: number = 1,
        distanceTolerance: number = 1e-3
    ): boolean {
        const isDegenerate = (s: LineSegment) => this.distanceBetweenPoints(s.p1, s.p2) <= Number.EPSILON;

        const s1Deg = isDegenerate(l1);
        const s2Deg = isDegenerate(l2);

        // Handle degenerate cases (points)
        if (s1Deg && s2Deg) {
            // Both are points: colinear if they are within distance tolerance
            return this.distanceBetweenPoints(l1.p1, l2.p1) <= distanceTolerance;
        }
        if (s1Deg) {
            // l1 is a point: check if that point lies on l2's line
            return this.pointToLineDistance(l1.p1, l2.p1, l2.p2) <= distanceTolerance;
        }
        if (s2Deg) {
            // l2 is a point: check if that point lies on l1's line
            return this.pointToLineDistance(l2.p1, l1.p1, l1.p2) <= distanceTolerance;
        }

        // Check near-parallel directions
        if (!this.areVectorsParallel(l1.p1, l1.p2, l2.p1, l2.p2, angleToleranceDeg)) {
            return false;
        }

        // Check both points of l2 lie close to l1's infinite line (or vice-versa for robustness)
        const d21 = this.pointToLineDistance(l2.p1, l1.p1, l1.p2);
        const d22 = this.pointToLineDistance(l2.p2, l1.p1, l1.p2);
        if (d21 <= distanceTolerance && d22 <= distanceTolerance) {
            return true;
        }

        const d11 = this.pointToLineDistance(l1.p1, l2.p1, l2.p2);
        const d12 = this.pointToLineDistance(l1.p2, l2.p1, l2.p2);
        return d11 <= distanceTolerance && d12 <= distanceTolerance;
    }

    public static inflatePolygonHV(
        poly: Array<CoordPoint>,
        horizontalSpacing: number,
        verticalSpacing: number,
        wallsWithCorrection?: Array<WallWithCorrection>,
        color?: string
    ) {
        // var offset = new Offset();
        // var horizontalPolygon = offset.data(poly.map(p => [p.x, p.y])).padding(horizontalSpacing)[0].map(p => ({x: p[0], y: p[1]})).reverse();
        // var verticalPolygon = offset.data(poly.map(p => [p.x, p.y])).padding(verticalSpacing)[0].map(p => ({x: p[0], y: p[1]})).reverse();

        // Avoids a bug when spacing = 0
        if (verticalSpacing === 0) {
            verticalSpacing += 0.0001;
        }
        if (horizontalSpacing === 0) {
            horizontalSpacing += 0.0001;
        }

        const horizontalPolygon = this.inflatePolygon(poly, horizontalSpacing).reverse();
        const verticalPolygon = this.inflatePolygon(poly, verticalSpacing).reverse();

        let mergedPolygon = [];
        for (let i = 0; i < horizontalPolygon.length; i++) {
            const v = Math.abs(verticalSpacing); //1
            console.log('mergedPolygon a', { horizontalPolygon, verticalPolygon, poly, i });

            //if (i <= verticalPolygon.length) {
            const vDiff = Math.abs(poly[i].y - verticalPolygon[i].y); //0.5
            if (vDiff < v) {
                if (poly[i].y < verticalPolygon[i].y) {
                    verticalPolygon[i].y += v - vDiff;
                } else {
                    verticalPolygon[i].y -= v - vDiff;
                }
            }
            //}

            const h = Math.abs(horizontalSpacing); //1
            const hDiff = Math.abs(Math.abs(poly[i].x) - Math.abs(horizontalPolygon[i].x)); //0.5
            if (hDiff < h) {
                if (poly[i].x < horizontalPolygon[i].x) {
                    horizontalPolygon[i].x += h - hDiff;
                } else {
                    horizontalPolygon[i].x -= h - hDiff;
                }
            }

            if (wallsWithCorrection && wallsWithCorrection.length) {
                for (const wallWithCorrection of wallsWithCorrection) {
                    if (Editor.testPointBelongToWall(wallWithCorrection.wall, poly[i])) {
                        const direction = WallUtils.wallDirection(wallWithCorrection.wall);
                        let correction = wallWithCorrection.correction;
                        if (direction === 'h') {
                            if (verticalSpacing < 0) {
                                correction = -correction;
                            }
                            if (poly[i].y < verticalPolygon[i].y) {
                                // BOTTOM walls
                                verticalPolygon[i].y += correction;
                            } else {
                                // TOP walls
                                verticalPolygon[i].y -= correction;
                            }
                        } else if (direction === 'v') {
                            if (horizontalSpacing < 0) {
                                correction = -correction;
                            }
                            if (poly[i].x < horizontalPolygon[i].x) {
                                // RIGHT walls
                                horizontalPolygon[i].x += correction;
                            } else {
                                // LEFT walls
                                horizontalPolygon[i].x -= correction;
                            }
                        }
                    }
                }
            }

            mergedPolygon.push({ x: horizontalPolygon[i].x, y: verticalPolygon[i].y });
        }

        return mergedPolygon;
    }

    /**
     * Returns true if the two polygons intersect. If the polygons are next to each other
     * and their only intersection is a line, returns false.
     */
    public static testPolygonsIntersect = (polyA: Array<CoordPoint> = [], polyB: Array<CoordPoint> = []) => {
        const intersection = this.calcIntersection(polyA, polyB);
        return intersection?.getCoordinates()?.length > 2;
    };

    /** Returns the jsts intersection object of the two polygons */
    public static calcIntersection = (polyA: Array<CoordPoint>, polyB: Array<CoordPoint>) => {
        const geometryFactory = JstsUtils.GeometryFactory();

        const geoInputPolyA = JstsUtils.vectorCoordinates2JTS(polyA);
        geoInputPolyA.push(geoInputPolyA[0]);
        const jstsPolyA = geometryFactory.createPolygon(geoInputPolyA);

        const geoInputPolyB = JstsUtils.vectorCoordinates2JTS(polyB);
        geoInputPolyB.push(geoInputPolyB[0]);
        const jstsPolyB = geometryFactory.createPolygon(geoInputPolyB);

        return jstsPolyA.intersection(jstsPolyB);
    };

    /** Returns true if the point is within the room (including on the room's walls).   */
    public static testPointWithinRoom = (point: CoordPoint, room: ILaizeRoom) => {
        const polygon = [room.coords!.map((point) => [point.x, point.y])];
        const isWithin = JstsUtils.within(point, polygon);
        let isOnWall = false;
        room.walls?.every((wall) => {
            if (Editor.testPointBelongToWall(wall, point)) {
                isOnWall = true;
                return false;
            }
            return true;
        });
        return isWithin || isOnWall;
    };

    public static getClosestPointIndex(point: CoordPoint, list: Array<CoordPoint>) {
        let distance = Infinity;
        let closestPoint: CoordPoint;
        for (const lp of list) {
            const measure = qSVG.measure(lp, point);
            if (measure < distance) {
                distance = measure;
                closestPoint = lp;
            }
            distance = measure < distance ? measure : distance;
        }
        return { closestPointDistance: distance, closestPointIndex: list.indexOf(closestPoint!) }; //list.sort((a, b) => Math.abs(number - a) - Math.abs(number - b))[0];
    }

    public static bboxCenter(points: Array<CoordPoint>) {
        const bbox = this.calculateBoundingBox({ points })!;
        return { x: (bbox.xMin + bbox.xMax) / 2, y: (bbox.yMin + bbox.yMax) / 2 };
    }

    /**
     * Gets the coordinates of a polygon that has multiple geometries.
     * (Happens when the intersection between two polygons results in more than one polygon).
     * @param {*} multiPoly
     * @returns
     */
    public static getMultiPolyCoords(multiPoly: JstsPolygon) {
        const multiPolyCoords: Array<CoordPoint> = [];
        for (let i = 0; i < multiPoly.getNumGeometries(); i++) {
            const subIntersection = multiPoly.getGeometryN(i);
            const subIntersectionCoords = subIntersection.getCoordinates();
            for (let j = 0; j < subIntersectionCoords.length; j++) {
                multiPolyCoords.push({
                    x: subIntersectionCoords[j].x,
                    y: subIntersectionCoords[j].y,
                });
            }
        }
        return multiPolyCoords;
    }

    public static testPointWithinPolygonWithoutWalls(point: CoordPoint, polygon: JstsPolygon, geometryFactory: any) {
        return polygon.contains(geometryFactory.createPoint(point));
    }

    public static testPointWithinPolygon(point: CoordPoint, polygon: JstsPolygon, geometryFactory?: any) {
        if (!geometryFactory) {
            geometryFactory = JstsUtils.GeometryFactory();
        }

        const isContained = PointUtils.testPointWithinPolygonWithoutWalls(point, polygon, geometryFactory);

        const polyWalls = this.toWalls(polygon.getCoordinates());
        let isOnWall = false;
        polyWalls.forEach((wall) => {
            if (Editor.testPointBelongToWall(wall, point)) {
                isOnWall = true;
            }
        });
        return isContained || isOnWall;
    }

    public static toWalls(coords: Array<CoordPoint>) {
        let walls: Array<WallLine> = [];
        for (let i = 0; i < coords.length - 1; i++) {
            const nextIndex = (i + 1) % coords.length;
            walls.push({ start: coords[i], end: coords[nextIndex], angleRad: qSVG.getAngle(coords[i], coords[nextIndex]).rad });
        }
        return walls;
    }

    public static isLineVertical(line: Array<CoordPoint>) {
        const angle = Math.atan2(line[1].y - line[0].y, line[1].x - line[0].x);
        const angleDeg = qSVG.angleRadToDeg(angle);
        return angleDeg === 90 || angleDeg === -90;
    }

    public static rotateCyclicCoords(coords: Array<CoordPoint>, rotationParams: RotationParams) {
        // Room coords have the same first and last coordinates, but
        // rotation of cyclic coordinates doesn't work well. So we pop
        // the last coord and add it back at the end.
        coords.pop();
        this.rotation(coords, rotationParams.angleDeg, rotationParams.center);
        coords.push(coords[0]);
    }

    // equivalent of excel mround function
    public static mround(number: number, multiple: number) {
        return multiple * Math.round(number / multiple);
    }

    /**
     * Returns a point coordinateC on the line passing through coordinateA and coordinateB,
     * where the distance from coordinateA to coordinateC equals the specified distance.
     *
     * @param coordinateA Reference point
     * @param distance Distance from coordinateA to coordinateC
     * @param coordinateB Another point on the same line
     * @returns Point coordinateC that is at specified distance from coordinateA on the line
     */
    public static pointAtDistanceOnLine(
        coordinateA: CoordPoint,
        distance: number,
        coordinateB: CoordPoint
    ): CoordPoint {
        // Calculate direction vector
        const directionX = coordinateB.x - coordinateA.x;
        const directionY = coordinateB.y - coordinateA.y;

        // Calculate the length of the direction vector
        const lineLength = Math.sqrt(directionX * directionX + directionY * directionY);

        // Avoid division by zero
        if (lineLength === 0) {
            return { ...coordinateA }; // Return copy of coordinateA if coordinateA and coordinateB are the same
        }

        // Normalize the direction vector
        const normalizedDirX = directionX / lineLength;
        const normalizedDirY = directionY / lineLength;

        // Calculate coordinateC by extending from coordinateA by the specified distance
        return {
            x: coordinateA.x + normalizedDirX * distance,
            y: coordinateA.y + normalizedDirY * distance,
        };
    }

    /**
     * Finds the point(s) B on a circle (centered at A with radius rA) where the distance BC equals the specified distance.
     * https://clicandposedev.blob.core.windows.net/screenshots/doc_compas_1.png
     * https://clicandposedev.blob.core.windows.net/screenshots/doc_compas_2.png
     * @param coordinateA Center of the circle
     * @param radiusA Radius of the circle
     * @param coordinateC Fixed point
     * @param distanceBC Desired distance between B and C
     * @returns Array of points B on the circle where distance BC equals distanceBC (0, 1, or 2 solutions)
     */
    public static findPointsOnCircleAtDistance(
        coordinateA: CoordPoint,
        radiusA: number,
        coordinateC: CoordPoint,
        distanceBC: number
    ): CoordPoint[] {
        // Distance from circle center A to point C
        const distanceAC = Math.sqrt(
            Math.pow(coordinateC.x - coordinateA.x, 2) + Math.pow(coordinateC.y - coordinateA.y, 2)
        );

        // Check if solution is geometrically possible
        // Triangle inequality: |AC - rA| <= BC <= AC + rA
        if (distanceBC < Math.abs(distanceAC - radiusA) || distanceBC > distanceAC + radiusA) {
            return []; // No solution
        }

        // Use the law of cosines to find angle BAC
        // BC² = AB² + AC² - 2(AB)(AC)cos(∠BAC)
        // distanceBC² = radiusA² + distanceAC² - 2(radiusA)(distanceAC)cos(∠BAC)
        const cosAngleBAC =
            (radiusA * radiusA + distanceAC * distanceAC - distanceBC * distanceBC) / (2 * radiusA * distanceAC);

        // Check if cosine is valid (between -1 and 1)
        if (cosAngleBAC < -1 || cosAngleBAC > 1) {
            return []; // No solution
        }

        const angleBAC = Math.acos(cosAngleBAC);

        // Calculate the angle from A to C
        const angleAC = Math.atan2(coordinateC.y - coordinateA.y, coordinateC.x - coordinateA.x);

        const solutions: CoordPoint[] = [];

        // First solution: rotate by +angleBAC from AC direction
        const angle1 = angleAC + angleBAC;
        solutions.push({
            x: coordinateA.x + radiusA * Math.cos(angle1),
            y: coordinateA.y + radiusA * Math.sin(angle1),
        });

        // Second solution: rotate by -angleBAC from AC direction (if angleBAC != 0)
        if (angleBAC !== 0) {
            const angle2 = angleAC - angleBAC;
            solutions.push({
                x: coordinateA.x + radiusA * Math.cos(angle2),
                y: coordinateA.y + radiusA * Math.sin(angle2),
            });
        }

        return solutions;
    }

    /**
     * Finds the point B on a circle (centered at A with radius rA) that is closest to point C.
     *
     * @param coordinateA Center of the circle
     * @param radiusA Radius of the circle
     * @param coordinateC Fixed point outside the circle
     * @returns Point B on the circle closest to C
     */
    public static findClosestPointOnCircle(
        coordinateA: CoordPoint,
        radiusA: number,
        coordinateC: CoordPoint
    ): CoordPoint {
        // Calculate direction vector from circle center A to point C
        const directionX = coordinateC.x - coordinateA.x;
        const directionY = coordinateC.y - coordinateA.y;

        // Calculate distance from A to C
        const distanceAC = Math.sqrt(directionX * directionX + directionY * directionY);

        // Handle edge case where C is at the center of the circle
        if (distanceAC === 0) {
            return {
                x: coordinateA.x + radiusA,
                y: coordinateA.y,
            };
        }

        // Normalize the direction vector
        const normalizedDirX = directionX / distanceAC;
        const normalizedDirY = directionY / distanceAC;

        // Find point B on the circle closest to C
        // B is at distance radiusA from A in the direction of C
        return {
            x: coordinateA.x + normalizedDirX * radiusA,
            y: coordinateA.y + normalizedDirY * radiusA,
        };
    }

    /**
     * Finds the point B on a circle (centered at A with radius rA) that is farthest from point C.
     *
     * @param coordinateA Center of the circle
     * @param radiusA Radius of the circle
     * @param coordinateC Fixed point outside the circle
     * @returns Point B on the circle farthest from C
     */
    public static findFarthestPointOnCircle(
        coordinateA: CoordPoint,
        radiusA: number,
        coordinateC: CoordPoint
    ): CoordPoint {
        // Calculate direction vector from circle center A to point C
        const directionX = coordinateC.x - coordinateA.x;
        const directionY = coordinateC.y - coordinateA.y;

        // Calculate distance from A to C
        const distanceAC = Math.sqrt(directionX * directionX + directionY * directionY);

        // Handle edge case where C is at the center of the circle
        if (distanceAC === 0) {
            return {
                x: coordinateA.x - radiusA,
                y: coordinateA.y,
            };
        }

        // Normalize the direction vector
        const normalizedDirX = directionX / distanceAC;
        const normalizedDirY = directionY / distanceAC;

        // Find point B on the circle farthest from C
        // B is at distance radiusA from A in the opposite direction of C
        return {
            x: coordinateA.x - normalizedDirX * radiusA,
            y: coordinateA.y - normalizedDirY * radiusA,
        };
    }

    /**
     * Filters all coordinate points that are inside a circle (centered at A with radius rA).
     *
     * @param coordinateA Center of the circle
     * @param radiusA Radius of the circle
     * @param points Array of points to filter
     * @param includeOnCircle If true, includes points exactly on the circle circumference (default: false)
     * @returns Array of points that are inside the circle
     */
    public static filterPointsInsideCircle(
        coordinateA: CoordPoint,
        radiusA: number,
        points: CoordPoint[],
        includeOnCircle: boolean = false
    ): CoordPoint[] {
        return points.filter((point) => {
            const distance = this.distanceBetweenPoints(coordinateA, point);
            return includeOnCircle ? distance <= radiusA : distance < radiusA;
        });
    }

    /**
     * Finds the intersection point of a line segment passing through the circle center (coordinateA)
     * and a point inside the circle (coordinateB) with the circle circumference.
     *
     * @param coordinateA Center of the circle
     * @param radiusA Radius of the circle
     * @param coordinateB Point inside the circle
     * @returns Intersection point on the circle circumference, or null if coordinateB is not inside the circle
     */
    public static findCircleIntersectionFromCenter(
        coordinateA: CoordPoint,
        radiusA: number,
        coordinateB: CoordPoint
    ): CoordPoint | null {
        // Calculate direction vector from center A to point B
        const directionX = coordinateB.x - coordinateA.x;
        const directionY = coordinateB.y - coordinateA.y;

        // Calculate distance from A to B
        const distanceAB = Math.sqrt(directionX * directionX + directionY * directionY);

        // Check if B is actually inside the circle (or at center)
        if (distanceAB >= radiusA) {
            return null; // Point B is not inside the circle
        }

        // Handle edge case where B is at the center of the circle
        if (distanceAB === 0) {
            // Return any point on the circle (e.g., rightmost point)
            return {
                x: coordinateA.x + radiusA,
                y: coordinateA.y,
            };
        }

        // Normalize the direction vector
        const normalizedDirX = directionX / distanceAB;
        const normalizedDirY = directionY / distanceAB;

        // Find intersection point by extending from A in the direction of B by radius distance
        return {
            x: coordinateA.x + normalizedDirX * radiusA,
            y: coordinateA.y + normalizedDirY * radiusA,
        };
    }

    public static filterGarbagePointsToMakeValidPolygon = (polygon: Array<CoordPoint>) => {
        const lastIndex = polygon?.findLastIndex((point) => point.x === polygon[0].x && point.y === polygon[0].y);
        return lastIndex !== -1 && lastIndex ? polygon.slice(0, lastIndex + 1) : polygon;
    };

    /**
     * Removes consecutive duplicate CoordPoint objects from an array.
     * @param points Array of CoordPoint
     * @returns Array with consecutive duplicates removed
     */
    public static filterConsecutiveDuplicatePoints(points: Array<CoordPoint>): Array<CoordPoint> {
        if (points.length === 0) return [];
        const filtered: CoordPoint[] = [points[0]];
        for (let i = 1; i < points.length; i++) {
            const prev = points[i - 1];
            const curr = points[i];
            if (prev.x !== curr.x || prev.y !== curr.y) {
                filtered.push(curr);
            }
        }
        return filtered;
    }
}
